import { useEffect, useState } from 'react';
import {
  fetchAppContext,
  fetchBrandContext,
  fetchComplianceSummary,
  fetchCustomerContext,
  fetchDebtor,
  fetchUserContext,
} from './web-api';

type ApiCallState<T> = {
  result?: T;
  loading: boolean;
  reload: () => void;
  error?: any;
};

type ApiCallResult<T> = [
  result: T | undefined,
  loading: boolean,
  error: any | undefined,
  reload: () => void,
];

export const useApi = <T extends any>(
  action: () => Promise<T>,
  defaultValue?: T,
  dependencies: any[] = []
): ApiCallResult<T> => {
  const [state, setState] = useState<ApiCallState<T>>({ loading: true, reload: () => { } });

  useEffect(() => {
    let isMounted = true;

    const wrapped = () => {
      setState(prev => ({ ...prev, loading: true }));
      action()
        .then((result: T) => (isMounted ? setState(prev => ({ ...prev, result, loading: false })) : null))
        .catch((error: any) => (isMounted ? setState(prev => ({ ...prev, result: defaultValue, error, loading: false })) : null));
    };

    setState(prev => ({ ...prev, reload: wrapped }));

    wrapped();

    return () => {
      isMounted = false;
    };
  }, dependencies);

  const { result, loading, error, reload } = state;
  return [result, loading, error, reload];
};

const createHook = <A extends any[], T>(apiCall: (...a: A) => Promise<T>): (...a: A) => ApiCallResult<T> => (...args: A) => useApi<T>(() => apiCall(...args));

export const useBrandContextQuery = createHook(fetchBrandContext);
export const useAppContextQuery = createHook(fetchAppContext);
export const useUserContextQuery = createHook(fetchUserContext);
export const useCustomerContextQuery = createHook(fetchCustomerContext);
export const useDebtorQuery = createHook(fetchDebtor);
export const useComplianceSummaryQuery = createHook(fetchComplianceSummary);
